import { styled } from "@linaria/react";
import React from "react";
import { Flipped, Flipper, spring } from "react-flip-toolkit";
import type { ToastType } from "../Toast";
import Toast from "../Toast";
import { zIndex } from "..";
import { HIDE_ON_EXPORT } from "../lib/downloadPage";

const width = 400;

const Container = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.toast};
  height: 100vh;
  width: ${width}px;
  padding: 10px;
  pointer-events: none;

  display: flex;
  flex-direction: column-reverse;

  > div + div {
    margin-bottom: 10px;
  }

  @media print {
    display: none;
  }
`;

interface Toast {
  id: number;
  type: ToastType;
  title?: string;
  content: React.ReactNode;
}

const Context = React.createContext<{
  addToast: (type: ToastType, content: React.ReactNode, title?: string) => () => void;
}>({
  addToast: () => () => {},
});

const handleAppear = (el: HTMLElement) =>
  spring({
    onUpdate: (val: number | Record<string, number>) => {
      if (typeof val !== "number") return;
      el.style.transform = `translateX(${width - val * width}px)`;
      el.style.opacity = String(val);
    },
  });

const handleExit = (el: HTMLElement, _index: number, removeElement: () => void) => {
  el.style.pointerEvents = "none";

  spring({
    onUpdate: (val: number | Record<string, number>) => {
      if (typeof val !== "number") return;
      el.style.transform = `translateX(${val * width}px)`;
      el.style.opacity = String(1 - val);
    },
    onComplete: removeElement,
  });
};

interface P {
  children: React.ReactNode;
}

let toastId = 0;

const ToastProvider = ({ children }: P) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const handleClose = React.useCallback((id: number) => {
    setToasts(toasts => toasts.filter(t => t.id !== id));
  }, []);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const addToast = React.useCallback(
    (type: ToastType, content: React.ReactNode, title?: string) => {
      const id = toastId++;
      setToasts(toasts => [
        ...toasts,
        {
          id,
          type,
          title,
          content,
        },
      ]);

      window.setTimeout(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);

      return () => setToasts(toasts => toasts.filter(t => t.id !== id));
    },
    [],
  );

  const value = React.useMemo(() => ({ addToast }), [addToast]);

  return (
    <Context.Provider value={value}>
      {children}
      <Flipper flipKey={toasts.map(t => t.id).join("/")}>
        <Container data-cy="toast-provider-container" className={HIDE_ON_EXPORT} ref={containerRef}>
          {toasts.map(({ id, type, content, title }) => (
            <Flipped key={id} flipId={id} onAppear={handleAppear} onExit={handleExit} stagger>
              <Toast id={id} type={type} content={content} title={title} onClose={handleClose} />
            </Flipped>
          ))}
        </Container>
      </Flipper>
    </Context.Provider>
  );
};

export default ToastProvider;

export { Context as ToastContext };
